import { useEffect, useState, type FormEvent } from 'react'
import { CheckCircle2, Cloud, Eye, EyeOff, Globe2, Key, Plus, RefreshCw, Shield, Trash2, Zap, BadgeCheck, XCircle } from 'lucide-react'
import { api } from '../../api/client'
import type { DNSConfigResult, DNSProviderAccount, DNSProviderVerifyResult, Domain } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

export function DNSPage() {
  const { t } = useI18n()
  const [accounts, setAccounts] = useState<DNSProviderAccount[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [provider, setProvider] = useState<'cloudflare' | 'tencent'>('cloudflare')
  const [accountName, setAccountName] = useState('')
  const [cfToken, setCfToken] = useState('')
  const [cfAccountId, setCfAccountId] = useState('')
  const [txSecretId, setTxSecretId] = useState('')
  const [txSecretKey, setTxSecretKey] = useState('')
  const [creating, setCreating] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [configDomainId, setConfigDomainId] = useState('')
  const [configAccountId, setConfigAccountId] = useState('')
  const [configuring, setConfiguring] = useState(false)
  const [configResults, setConfigResults] = useState<DNSConfigResult[] | null>(null)
  const [verifying, setVerifying] = useState<string | null>(null)
  const [verifyResults, setVerifyResults] = useState<Record<string, DNSProviderVerifyResult>>({})

  const load = async () => {
    try {
      const [accs, doms] = await Promise.all([
        api.get<DNSProviderAccount[]>('/admin/dns-providers'),
        api.get<Domain[]>('/admin/domains'),
      ])
      setAccounts(accs)
      setDomains(doms)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    }
  }

  useEffect(() => { void load() }, [])

  async function createAccount(event: FormEvent) {
    event.preventDefault()
    setError('')
    setCreating(true)
    try {
      const credentials = provider === 'cloudflare'
        ? { apiToken: cfToken, accountId: cfAccountId }
        : { secretId: txSecretId, secretKey: txSecretKey }
      const item = await api.post<DNSProviderAccount>('/admin/dns-providers', { provider, name: accountName, credentials })
      setAccounts(prev => [item, ...prev])
      setShowForm(false)
      setAccountName('')
      setCfToken('')
      setCfAccountId('')
      setTxSecretId('')
      setTxSecretKey('')
      setSuccess(t('admin.dnsProviderCreated'))
      setTimeout(() => setSuccess(''), 3000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create account')
    } finally {
      setCreating(false)
    }
  }

  async function deleteAccount(id: string) {
    if (!confirm(t('admin.dnsProviderDeleteConfirm'))) return
    try {
      await api.delete(`/admin/dns-providers/${id}`)
      setAccounts(prev => prev.filter(a => a.id !== id))
      setVerifyResults(prev => { const next = { ...prev }; delete next[id]; return next })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  async function verifyAccount(id: string) {
    setVerifying(id)
    setError('')
    try {
      const result = await api.post<DNSProviderVerifyResult>('/admin/dns-verify', { accountId: id })
      setVerifyResults(prev => ({ ...prev, [id]: result }))
      if (result.valid) {
        setSuccess(t('admin.dnsVerifySuccess'))
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verification failed')
    } finally {
      setVerifying(null)
    }
  }

  async function configureDNS(event: FormEvent) {
    event.preventDefault()
    if (!configDomainId || !configAccountId) return
    setConfiguring(true)
    setError('')
    setConfigResults(null)
    try {
      const res = await api.post<{ results: DNSConfigResult[]; error?: string }>('/admin/dns-configure', { domainId: configDomainId, accountId: configAccountId })
      setConfigResults(res.results)
      if (res.error) setError(res.error)
      else {
        setSuccess(t('admin.dnsConfigSuccess'))
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Configuration failed')
    } finally {
      setConfiguring(false)
    }
  }

  const providerIcon = (p: string) => p === 'cloudflare' ? <Cloud size={16} className="text-orange-500" /> : <Shield size={16} className="text-blue-500" />
  const providerLabel = (p: string) => p === 'cloudflare' ? 'Cloudflare' : t('admin.dnsProviderTencent')

  return <>
    <PageHeader title="admin.dnsManagement" action={
      <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
        <Plus size={16} />{t('admin.dnsAddProvider')}
      </button>
    } />

    {error && <p className="mb-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive">{error}</p>}
    {success && <p className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-700">{success}</p>}

    {showForm && (
      <div className="panel mb-6 p-5 sm:p-6 border-primary/20">
        <div className="flex items-center gap-2 mb-4">
          <Key size={18} className="text-primary" />
          <h2 className="text-base font-semibold">{t('admin.dnsAddProviderTitle')}</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('admin.dnsAddProviderDesc')}</p>

        <form onSubmit={createAccount} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">
              {t('admin.dnsProviderType')}
              <select value={provider} onChange={e => setProvider(e.target.value as 'cloudflare' | 'tencent')} className="field mt-1.5">
                <option value="cloudflare">Cloudflare</option>
                <option value="tencent">{t('admin.dnsProviderTencent')}</option>
              </select>
            </label>
            <label className="text-sm font-medium">
              {t('admin.dnsAccountName')}
              <input className="field mt-1.5" value={accountName} onChange={e => setAccountName(e.target.value)} placeholder={t('admin.dnsAccountNamePlaceholder')} required />
            </label>
          </div>

          {provider === 'cloudflare' ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium">
                API Token
                <div className="relative mt-1.5">
                  <input type={showSecret ? 'text' : 'password'} className="field pr-10" value={cfToken} onChange={e => setCfToken(e.target.value)} placeholder="Bearer token" required />
                  <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>
              <label className="text-sm font-medium">
                Account ID
                <input className="field mt-1.5" value={cfAccountId} onChange={e => setCfAccountId(e.target.value)} placeholder="Account identifier" required />
              </label>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium">
                SecretId
                <input className="field mt-1.5" value={txSecretId} onChange={e => setTxSecretId(e.target.value)} placeholder="AKIDxxxxxxxx" required />
              </label>
              <label className="text-sm font-medium">
                SecretKey
                <div className="relative mt-1.5">
                  <input type={showSecret ? 'text' : 'password'} className="field pr-10" value={txSecretKey} onChange={e => setTxSecretKey(e.target.value)} placeholder="Secret key" required />
                  <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </label>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button disabled={creating} className="btn-primary">{creating ? t('common.loading') : t('admin.dnsAddProvider')}</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">{t('common.cancel')}</button>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Shield size={12} />{t('admin.dnsSecurityNote')}</p>
        </form>
      </div>
    )}

    {/* Provider accounts list */}
    <section className="mb-8">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('admin.dnsProviderAccounts')}</h2>
      {accounts.length === 0 ? (
        <EmptyState title="admin.dnsEmptyProviders" />
      ) : (
        <div className="space-y-3">
          {accounts.map(acc => (
            <div key={acc.id} className="panel p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {providerIcon(acc.provider)}
                  <div>
                    <p className="font-medium text-sm">{acc.name}</p>
                    <p className="text-xs text-muted-foreground">{providerLabel(acc.provider)} · {acc.credentialsSet ? <span className="text-emerald-600">{t('admin.dnsCredentialsSet')}</span> : <span className="text-amber-600">{t('admin.dnsCredentialsNotSet')}</span>}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => verifyAccount(acc.id)} disabled={verifying === acc.id} className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-muted/50 transition-colors flex items-center gap-1.5 disabled:opacity-50" aria-label={t('admin.dnsVerifyBtn')}>
                    {verifying === acc.id ? <RefreshCw size={13} className="animate-spin" /> : <BadgeCheck size={13} />}
                    {t('admin.dnsVerifyBtn')}
                  </button>
                  <button onClick={() => deleteAccount(acc.id)} className="rounded-lg border p-2 text-destructive hover:bg-destructive/10 transition-colors" aria-label="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {verifyResults[acc.id] && (
                <div className={`mt-3 rounded-lg border px-4 py-3 ${verifyResults[acc.id].valid ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {verifyResults[acc.id].valid ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-red-500" />}
                    <span className="text-sm font-medium">{verifyResults[acc.id].valid ? t('admin.dnsVerifyValid') : t('admin.dnsVerifyInvalid')}</span>
                    {verifyResults[acc.id].message && <span className="text-xs text-muted-foreground ml-2">{verifyResults[acc.id].message}</span>}
                  </div>
                  {verifyResults[acc.id].valid && verifyResults[acc.id].domains.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">{t('admin.dnsAvailableDomains')} ({verifyResults[acc.id].domains.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {verifyResults[acc.id].domains.map(d => (
                          <span key={d} className="inline-flex items-center gap-1 rounded-md bg-white border px-2 py-0.5 text-xs font-mono">
                            <Globe2 size={11} className="text-muted-foreground" />{d}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>

    {/* One-click DNS configuration */}
    <section>
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('admin.dnsOneClick')}</h2>
      <div className="panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={18} className="text-primary" />
          <h3 className="text-base font-semibold">{t('admin.dnsOneClickTitle')}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{t('admin.dnsOneClickDesc')}</p>

        <form onSubmit={configureDNS} className="grid gap-4 sm:grid-cols-[1fr_1fr_auto]">
          <label className="text-sm font-medium">
            {t('admin.dnsSelectDomain')}
            <select value={configDomainId} onChange={e => setConfigDomainId(e.target.value)} className="field mt-1.5" required>
              <option value="">{t('admin.dnsSelectDomainPlaceholder')}</option>
              {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium">
            {t('admin.dnsSelectProvider')}
            <select value={configAccountId} onChange={e => setConfigAccountId(e.target.value)} className="field mt-1.5" required>
              <option value="">{t('admin.dnsSelectProviderPlaceholder')}</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({providerLabel(a.provider)})</option>)}
            </select>
          </label>
          <div className="flex items-end">
            <button disabled={configuring || !configDomainId || !configAccountId} className="btn-primary h-10 flex items-center gap-2">
              {configuring ? <RefreshCw size={14} className="animate-spin" /> : <Globe2 size={14} />}
              {configuring ? t('common.loading') : t('admin.dnsConfigureBtn')}
            </button>
          </div>
        </form>

        {configResults && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium">{t('admin.dnsConfigResults')}</h4>
            {configResults.map((r, i) => (
              <div key={i} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${r.status === 'success' ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                {r.status === 'success' ? <CheckCircle2 size={14} className="text-emerald-500" /> : <span className="text-red-500 text-xs">✗</span>}
                <span className="font-medium">{r.recordType}</span>
                <span className="text-muted-foreground">{r.host}</span>
                {r.error && <span className="text-xs text-red-600 ml-auto truncate max-w-[300px]">{r.error}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  </>
}
